import React, { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from "react";
import type {
  VariableFile,
  VariableSource,
  PreviewMode,
  ColumnMapping,
  UploadedFileData,
  MatchingResult,
} from "../types";

interface UploadedCSV {
  name: string;
  columns: string[];
  data: any[];
}

interface VariablesContextType {
  // Existing state (backward compat with ADM builder)
  activeVariables: Set<string>;
  setActiveVariables: (variables: Set<string>) => void;
  addActiveVariable: (variable: string) => void;
  removeActiveVariable: (variable: string) => void;
  clearActiveVariables: () => void;
  getActiveVariablesArray: () => string[];
  selectedMailingList: string | null;
  setSelectedMailingList: Dispatch<SetStateAction<string | null>>;
  selectedCouponList: string | null;
  setSelectedCouponList: Dispatch<SetStateAction<string | null>>;
  uploadedMailingLists: UploadedCSV[];
  setUploadedMailingLists: Dispatch<SetStateAction<UploadedCSV[]>>;
  uploadedCouponLists: UploadedCSV[];
  setUploadedCouponLists: Dispatch<SetStateAction<UploadedCSV[]>>;

  // New state for Variables feature
  variableSource: VariableSource;
  setVariableSource: Dispatch<SetStateAction<VariableSource>>;
  selectedVariableFile: VariableFile | null;
  setSelectedVariableFile: Dispatch<SetStateAction<VariableFile | null>>;
  existingVariableFiles: VariableFile[];
  setExistingVariableFiles: Dispatch<SetStateAction<VariableFile[]>>;
  uploadedFileData: UploadedFileData | null;
  setUploadedFileData: Dispatch<SetStateAction<UploadedFileData | null>>;
  columnMappings: ColumnMapping[];
  setColumnMappings: Dispatch<SetStateAction<ColumnMapping[]>>;
  variableData: Record<string, string[]>;
  setVariableData: Dispatch<SetStateAction<Record<string, string[]>>>;
  previewMode: PreviewMode;
  setPreviewMode: Dispatch<SetStateAction<PreviewMode>>;
  matchingResult: MatchingResult | null;
  setMatchingResult: Dispatch<SetStateAction<MatchingResult | null>>;
  isLoading: boolean;
  setIsLoading: Dispatch<SetStateAction<boolean>>;

  // Persistent selection state (survives tab switches)
  selectedDefaultPlaceholders: string[];
  setSelectedDefaultPlaceholders: Dispatch<SetStateAction<string[]>>;
  selectedCustomVariables: string[];
  setSelectedCustomVariables: Dispatch<SetStateAction<string[]>>;
  selectedExistingPlaceholders: string[];
  setSelectedExistingPlaceholders: Dispatch<SetStateAction<string[]>>;
  selectedAudiencePlaceholders: string[];
  setSelectedAudiencePlaceholders: Dispatch<SetStateAction<string[]>>;
  customPlaceholders: string[];
  setCustomPlaceholders: Dispatch<SetStateAction<string[]>>;
  existingFileData: Record<string, string>[];
  setExistingFileData: Dispatch<SetStateAction<Record<string, string>[]>>;
}

const VariablesContext = createContext<VariablesContextType | undefined>(undefined);

interface VariablesProviderProps {
  children: ReactNode;
}

export const VariablesProvider: React.FC<VariablesProviderProps> = ({ children }) => {
  // Existing state
  const [activeVariables, setActiveVariables] = useState<Set<string>>(new Set());
  const [selectedMailingList, setSelectedMailingList] = useState<string | null>(null);
  const [selectedCouponList, setSelectedCouponList] = useState<string | null>(null);
  const [uploadedMailingLists, setUploadedMailingLists] = useState<UploadedCSV[]>([]);
  const [uploadedCouponLists, setUploadedCouponLists] = useState<UploadedCSV[]>([]);

  // New state for Variables feature
  const [variableSource, setVariableSource] = useState<VariableSource>("default");
  const [selectedVariableFile, setSelectedVariableFile] = useState<VariableFile | null>(null);
  const [existingVariableFiles, setExistingVariableFiles] = useState<VariableFile[]>([]);
  const [uploadedFileData, setUploadedFileData] = useState<UploadedFileData | null>(null);
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [variableData, setVariableData] = useState<Record<string, string[]>>({});
  const [previewMode, setPreviewMode] = useState<PreviewMode>("none");
  const [matchingResult, setMatchingResult] = useState<MatchingResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Persistent selection state
  const [selectedDefaultPlaceholders, setSelectedDefaultPlaceholders] = useState<string[]>([]);
  const [selectedCustomVariables, setSelectedCustomVariables] = useState<string[]>([]);
  const [selectedExistingPlaceholders, setSelectedExistingPlaceholders] = useState<string[]>([]);
  const [selectedAudiencePlaceholders, setSelectedAudiencePlaceholders] = useState<string[]>([]);
  const [customPlaceholders, setCustomPlaceholders] = useState<string[]>([]);
  const [existingFileData, setExistingFileData] = useState<Record<string, string>[]>([]);

  const addActiveVariable = (variable: string) => {
    setActiveVariables((prev) => new Set([...Array.from(prev), variable]));
  };

  const removeActiveVariable = (variable: string) => {
    setActiveVariables((prev) => {
      const newSet = new Set(prev);
      newSet.delete(variable);
      return newSet;
    });
  };

  const clearActiveVariables = () => {
    setActiveVariables(new Set());
  };

  const getActiveVariablesArray = () => {
    return Array.from(activeVariables);
  };

  const value: VariablesContextType = {
    activeVariables,
    setActiveVariables,
    addActiveVariable,
    removeActiveVariable,
    clearActiveVariables,
    getActiveVariablesArray,
    selectedMailingList,
    setSelectedMailingList,
    selectedCouponList,
    setSelectedCouponList,
    uploadedMailingLists,
    setUploadedMailingLists,
    uploadedCouponLists,
    setUploadedCouponLists,
    // New state
    variableSource,
    setVariableSource,
    selectedVariableFile,
    setSelectedVariableFile,
    existingVariableFiles,
    setExistingVariableFiles,
    uploadedFileData,
    setUploadedFileData,
    columnMappings,
    setColumnMappings,
    variableData,
    setVariableData,
    previewMode,
    setPreviewMode,
    matchingResult,
    setMatchingResult,
    isLoading,
    setIsLoading,
    // Persistent selection state
    selectedDefaultPlaceholders,
    setSelectedDefaultPlaceholders,
    selectedCustomVariables,
    setSelectedCustomVariables,
    selectedExistingPlaceholders,
    setSelectedExistingPlaceholders,
    selectedAudiencePlaceholders,
    setSelectedAudiencePlaceholders,
    customPlaceholders,
    setCustomPlaceholders,
    existingFileData,
    setExistingFileData,
  };

  return <VariablesContext.Provider value={value}>{children}</VariablesContext.Provider>;
};

export const useVariables = (): VariablesContextType => {
  const context = useContext(VariablesContext);
  if (context === undefined) {
    throw new Error("useVariables must be used within a VariablesProvider");
  }
  return context;
};
